/**
 * Translation Workspace Page
 * Features: source text input or file upload, target language selection, multi-language results.
 */

import { Box, Button, IconButton, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import { Languages, Mic, Upload } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSupportedLanguages, translateText } from '../../services/api';
import { Agent, Language, SUPPORTED_LANGUAGES, TranslationResult } from '../../types';
import { downloadTranslationFile } from './api/translateFile.api';
import { FileJobProgress } from './components/FileJobProgress';
import { SourceFileChip } from './components/SourceFileChip';
import { TargetLanguageSelector } from './components/TargetLanguageSelector';
import { TranslationResultsPanel } from './components/TranslationResultsPanel';
import { TRANSLATE_FILE_ACCEPT } from './constants';
import { useTranslateFile } from './hooks/useTranslateFile';
import { useTranslateFileJob } from './hooks/useTranslateFileJob';

interface TranslationPageProps {
  agents: Agent[];
  onCreateAgent?: () => void;
  selectedAgentId?: string;
}

export const TranslationPage: React.FC<TranslationPageProps> = ({
  agents,
  onCreateAgent: _onCreateAgent,
  selectedAgentId: propSelectedAgentId,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<string>(
    propSelectedAgentId || agents.find((agent) => agent.isDefault)?.id || agents[0]?.id || ''
  );
  const [sourceText, setSourceText] = useState('');
  const [selectedTargetCodes, setSelectedTargetCodes] = useState<string[]>(['en', 'vi', 'ko']);
  const [supportedLanguages, setSupportedLanguages] = useState<Language[]>(SUPPORTED_LANGUAGES);
  const [results, setResults] = useState<TranslationResult[]>([]);
  const [isTranslating, setIsTranslating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {
    file: selectedFile,
    errorMessage: fileErrorMessage,
    selectFile,
    clearFile,
    clearError: clearFileError,
  } = useTranslateFile();

  const selectedAgent = useMemo(
    () => agents.find((agent) => agent.id === selectedAgentId),
    [agents, selectedAgentId]
  );
  const selectedTargets = useMemo(
    () => supportedLanguages.filter((language) => selectedTargetCodes.includes(language.code)),
    [selectedTargetCodes, supportedLanguages]
  );

  useEffect(() => {
    const defaultAgentId =
      propSelectedAgentId || agents.find((agent) => agent.isDefault)?.id || agents[0]?.id || '';

    setSelectedAgentId((currentAgentId) => {
      if (currentAgentId && agents.some((agent) => agent.id === currentAgentId)) {
        return currentAgentId;
      }

      return defaultAgentId;
    });
  }, [agents, propSelectedAgentId]);

  const findLanguage = (code: string): Language =>
    supportedLanguages.find((language) => language.code === code) || {
      code,
      name: code.toUpperCase(),
      nativeName: code.toUpperCase(),
    };

  const fileJob = useTranslateFileJob({
    agentId: selectedAgentId,
    findLanguage,
    onResults: setResults,
  });

  useEffect(() => {
    let isMounted = true;

    const loadSupportedLanguages = async () => {
      const response = await getSupportedLanguages();
      if (isMounted && response.success && response.data && response.data.length > 0) {
        setSupportedLanguages(response.data);
      }
    };

    void loadSupportedLanguages();

    return () => {
      isMounted = false;
    };
  }, []);

  const toggleTargetLanguage = useCallback((code: string) => {
    setSelectedTargetCodes((current) => {
      if (current.includes(code)) {
        return current.filter((item) => item !== code);
      }
      return [...current, code];
    });
  }, []);

  const buildPendingResults = (sourceLabel: string): TranslationResult[] =>
    selectedTargets.map((language) => ({
      id: `result-${Date.now()}-${language.code}`,
      targetLanguage: language,
      translatedText: '',
      sourceText: sourceLabel,
      sourceLanguage: findLanguage('en'),
      agentId: selectedAgentId,
      status: 'processing',
      createdAt: new Date(),
    }));

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0] || null;
    const accepted = selectFile(nextFile);
    event.target.value = '';

    if (accepted) {
      setSourceText('');
      setErrorMessage(null);
    }
  };

  const handleTranslate = async () => {
    const text = sourceText.trim();
    if ((!selectedFile && !text) || selectedTargets.length === 0) return;

    setErrorMessage(null);
    clearFileError();
    fileJob.clearMessages();

    if (selectedFile) {
      void fileJob.start({
        file: selectedFile,
        targetLang: selectedTargets.map((language) => language.code),
        agentPublicId: selectedAgent?.publicId,
      });
      return;
    }

    setIsTranslating(true);
    setResults(buildPendingResults(text));
    const response = await translateText({
      text,
      targetLang: selectedTargets.map((language) => language.code),
      agentPublicId: selectedAgent?.publicId,
    });

    if (!response.success || !response.data) {
      setErrorMessage(response.error || t('translate.errors.failed'));
      setResults((current) => current.map((result) => ({ ...result, status: 'failed' })));
      setIsTranslating(false);
      return;
    }

    const translatedResults: TranslationResult[] = response.data.translations.map(
      (translation) => ({
        id: `${response.data?.requestId}-${translation.lang}`,
        targetLanguage: findLanguage(translation.lang),
        translatedText: translation.text,
        sourceText: response.data?.source.text || text,
        sourceLanguage:
          response.data?.source.lang && response.data.source.lang !== 'auto'
            ? findLanguage(response.data.source.lang)
            : findLanguage('en'),
        agentId: selectedAgentId,
        status: translation.status === 'failed' ? 'failed' : 'completed',
        createdAt: new Date(),
      })
    );

    setResults(translatedResults);
    setIsTranslating(false);
  };

  const handleDownload = useCallback(
    async (result: TranslationResult) => {
      if (!result.download) return;

      try {
        await downloadTranslationFile(result.download.downloadUrl, result.download.fileName);
      } catch {
        setErrorMessage(t('translate.errors.downloadFailed'));
      }
    },
    [t]
  );

  const isBusy = isTranslating || fileJob.isBusy;
  const hasSource = Boolean(selectedFile || sourceText.trim());
  const sourcePlaceholder = selectedFile
    ? t('translate.source.fileSelectedPlaceholder')
    : t('translate.source.placeholder');
  const uploadLabel = selectedFile
    ? t('translate.source.replaceFile')
    : t('translate.source.uploadFile');

  return (
    <Box className="ambient-surface flex h-full min-h-0 flex-col overflow-hidden bg-gray-50 dark:bg-slate-900">
      <Box className="grid min-h-0 flex-1 grid-cols-1 grid-rows-2 gap-6 px-6 py-6 lg:grid-cols-2 lg:grid-rows-1 lg:items-start">
        <Box className="flex min-h-[420px] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800 lg:sticky lg:top-[5.5rem] lg:h-[calc(100dvh-7rem)]">
          <Box className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-slate-700">
            <Box className="flex items-center gap-2">
              <Languages size={20} className="text-blue-600 dark:text-blue-400" />
              <Typography
                variant="subtitle1"
                className="font-semibold text-gray-900 dark:text-white"
              >
                {t('translate.source.title')}
              </Typography>
            </Box>

            {agents.length > 0 && (
              <Select
                size="small"
                value={selectedAgentId}
                onChange={(event) => setSelectedAgentId(event.target.value)}
                className="min-w-[180px] bg-gray-100 text-sm text-gray-900 dark:bg-slate-700 dark:text-white"
              >
                {agents.map((agent) => (
                  <MenuItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>

          <Box className="flex min-h-0 flex-1 flex-col overflow-hidden p-4">
            {selectedFile && (
              <SourceFileChip file={selectedFile} disabled={isBusy} onRemove={clearFile} />
            )}
            <textarea
              aria-label={t('translate.source.title')}
              placeholder={sourcePlaceholder}
              value={sourceText}
              onChange={(event) => setSourceText(event.target.value)}
              disabled={Boolean(selectedFile)}
              className="h-full min-h-0 w-full flex-1 resize-none border-0 bg-transparent p-0 text-base leading-relaxed text-gray-900 outline-none placeholder:text-gray-400 disabled:cursor-not-allowed disabled:text-gray-500 dark:text-slate-300 dark:placeholder:text-slate-500 dark:disabled:text-slate-500"
            />
          </Box>

          <Box className="shrink-0 border-t border-gray-200 px-4 py-3 dark:border-slate-700">
            <Box className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <Box className="flex flex-wrap gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept={TRANSLATE_FILE_ACCEPT}
                  onChange={handleFileInputChange}
                />
                <Tooltip title={t('translate.source.supportedFormats')}>
                  <span>
                    <Button
                      size="small"
                      startIcon={<Upload size={16} />}
                      disabled={isBusy}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {uploadLabel}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={t('translate.source.voiceInput')}>
                  <span>
                    <IconButton
                      size="small"
                      disabled
                      className="text-blue-600 hover:bg-gray-100 disabled:text-gray-400 dark:text-blue-400 dark:hover:bg-slate-700 dark:disabled:text-slate-500"
                    >
                      <Mic size={18} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              {!selectedFile && (
                <Typography variant="caption" className="text-gray-500 dark:text-slate-500">
                  {t('translate.source.characterCount', { count: sourceText.length, limit: 5000 })}
                </Typography>
              )}
            </Box>
            <Button
              variant="contained"
              fullWidth
              onClick={handleTranslate}
              disabled={!hasSource || selectedTargets.length === 0 || isBusy}
              className="bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-500 dark:disabled:bg-slate-700 dark:disabled:text-slate-500"
              size="large"
            >
              {isBusy ? t('translate.actions.translating') : t('translate.actions.translate')}
            </Button>
          </Box>
        </Box>

        <Box className="flex max-h-[80dvh] min-h-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800 lg:sticky lg:top-[5.5rem] lg:h-[calc(100dvh-7rem)] lg:max-h-none">
          <Box className="flex min-h-[57px] shrink-0 items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-slate-700">
            <Box className="flex min-w-0 items-center gap-2">
              <Languages size={20} className="text-blue-600 dark:text-blue-400" />
              <Typography
                variant="subtitle1"
                className="font-semibold text-gray-900 dark:text-white"
              >
                {t('translate.response.title')}
              </Typography>
            </Box>
          </Box>

          <TargetLanguageSelector
            selectedTargets={selectedTargets}
            supportedLanguages={supportedLanguages}
            selectedTargetCodes={selectedTargetCodes}
            onToggleTargetLanguage={toggleTargetLanguage}
          />

          <TranslationResultsPanel
            errorMessage={errorMessage || fileErrorMessage || fileJob.errorMessage}
            infoMessage={fileJob.infoMessage}
            jobProgress={
              fileJob.isActive && fileJob.job ? (
                <FileJobProgress
                  job={fileJob.job}
                  cancelling={fileJob.cancelling}
                  onCancel={fileJob.cancel}
                />
              ) : null
            }
            results={results}
            onDownload={handleDownload}
          />
        </Box>
      </Box>
    </Box>
  );
};
